import 'core-js/modules/es.object.assign.js';
import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { Modal, ModalTitle, Text, ModalDescription, ModalActions, Stack, Button } from '@hub-la/design-system';
import { useGetBusiness, VerificationStatus } from '@hub-la/fe-get-user';
import { useRoleplay } from '@hub-la/fe-roleplay';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateAccountModal } from '../pages/update-account-modal.js';
import { AccountVerificationProvider } from '../providers/account-verification-provider.js';

const ConfirmationModal = props => {
  const {
    open,
    onSubmit,
    onCancel
  } = props;
  const {
    t
  } = useTranslation();
  return jsx(Modal, Object.assign({
    open: open
  }, {
    children: jsxs(Fragment, {
      children: [jsx(ModalTitle, {
        children: jsx(Text, Object.assign({
          variant: "h3"
        }, {
          children: t('confirmationModal.update.title')
        }))
      }), jsx(ModalDescription, {
        children: jsx(Text, Object.assign({
          variant: "body2"
        }, {
          children: t('confirmationModal.update.message')
        }))
      }), jsx(ModalActions, Object.assign({
        align: "right"
      }, {
        children: jsxs(Stack, Object.assign({
          direction: ['column-reverse', 'row']
        }, {
          children: [jsx(Button, Object.assign({
            variant: "text",
            marginRight: 4,
            onClick: onCancel
          }, {
            children: t('confirmationModal.update.cancel')
          })), jsx(Button, Object.assign({
            variant: "filled",
            hierarchy: "primary",
            onClick: onSubmit
          }, {
            children: t('confirmationModal.update.confirm')
          }))]
        }))
      }))]
    })
  }));
};
const RoleplayModal = props => {
  const {
    open,
    onCancel
  } = props;
  const {
    t
  } = useTranslation();
  return jsx(Modal, Object.assign({
    open: open
  }, {
    children: jsxs(Fragment, {
      children: [jsx(ModalTitle, {
        children: jsx(Text, Object.assign({
          variant: "h3"
        }, {
          children: t('roleplayModal.update.title')
        }))
      }), jsx(ModalDescription, {
        children: jsx(Text, Object.assign({
          variant: "body2"
        }, {
          children: t('roleplayModal.update.message')
        }))
      }), jsx(ModalActions, Object.assign({
        align: "right"
      }, {
        children: jsx(Button, Object.assign({
          variant: "text",
          marginRight: 4,
          onClick: onCancel
        }, {
          children: t('roleplayModal.update.cancel')
        }))
      }))]
    })
  }));
};
/**
 * HOC for checking if the user's account is approved before do any action.
 *
 * This higher-order component (HOC) wraps a button component and opens an update
 * account modal when the button is clicked and the user's account is not approved.
 *
 * @param {React.ComponentType<P>} WrappedComponent - The component to be wrapped.
 * @returns {React.FC<P & WithApprovedAccountProps>} The wrapped component.
 *
 * @example
 * // Import the HOC and your button component
 * import { withApprovedAccount } from '@hub-la/fe-account-verification'
 * import ButtonComponent from './ButtonComponent'
 *
 * // Wrap your button component using the HOC
 * const ButtonWithApprovedAccount = withApprovedAccount(ButtonComponent)
 */
const withApprovedAccount = WrappedComponent => {
  return props => {
    var _a;
    const [updateAccountOpen, setUpdateAccountOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [roleplayOpen, setRoleplayOpen] = useState(false);
    const {
      isRoleplay
    } = useRoleplay();
    const {
      data: account
    } = useGetBusiness();
    const isApproved = ((_a = account === null || account === void 0 ? void 0 : account.bankAccount) === null || _a === void 0 ? void 0 : _a.verificationStatus) === VerificationStatus.APPROVED;
    const openConfirmationModal = () => {
      var _a;
      if (isRoleplay && !isApproved) {
        setRoleplayOpen(true);
        return;
      }
      if (isApproved) {
        (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
        return;
      }
      setConfirmationOpen(true);
    };
    const closeConfirmationModal = () => {
      setConfirmationOpen(false);
    };
    const closeRoleplayModal = () => {
      setRoleplayOpen(false);
    };
    const openUpdateAccountModal = () => {
      setConfirmationOpen(false);
      setUpdateAccountOpen(true);
    };
    const closeUpdateAccountModal = () => {
      setUpdateAccountOpen(false);
      setConfirmationOpen(false);
    };
    const onFinish = () => {
      var _a;
      closeUpdateAccountModal();
      (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props);
    };
    return jsxs(AccountVerificationProvider, {
      children: [jsx(WrappedComponent, Object.assign({}, props, {
        onClick: openConfirmationModal
      })), jsx(ConfirmationModal, {
        open: confirmationOpen,
        onSubmit: openUpdateAccountModal,
        onCancel: closeConfirmationModal
      }), jsx(RoleplayModal, {
        open: roleplayOpen,
        onCancel: closeRoleplayModal
      }), jsx(UpdateAccountModal, {
        open: updateAccountOpen,
        onClose: closeUpdateAccountModal,
        onUpdate: onFinish
      })]
    });
  };
};

export { withApprovedAccount };
